import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Locale trees are fetched per visitor, not bundled. Statically importing all
// three put 817 KB of JSON — about a third of the raw entry bundle — in front
// of every first paint, two thirds of which nobody reads. The three files are
// key-for-key complete, so loading only the active one never falls through to a
// missing key.
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  'en-US': () => import(/* webpackChunkName: "locale-en" */ './locales/en-US.json'),
  'zh-CN': () => import(/* webpackChunkName: "locale-zh" */ './locales/zh-CN.json'),
  'ja-JP': () => import(/* webpackChunkName: "locale-ja" */ './locales/ja-JP.json'),
};

/* Old inline translations - replaced by JSON imports
const resources_old = {
  'en-US': {
    translation: {
      "common": {
        "loading": "Loading...",
        "save": "Save",
        "cancel": "Cancel",
        "delete": "Delete",
        "edit": "Edit",
        "add": "Add",
        "search": "Search",
        "filter": "Filter",
        "refresh": "Refresh",
        "confirm": "Confirm",
        "back": "Back",
        "next": "Next",
        "previous": "Previous",
        "submit": "Submit",
        "reset": "Reset",
        "close": "Close",
        "open": "Open",
        "yes": "Yes",
        "no": "No",
        "ok": "OK",
        "error": "Error",
        "success": "Success",
        "warning": "Warning",
        "info": "Information",
                "view": "View",
                "viewAll": "View All",
                "copy": "Copy",
                "total": "Total",
                "update": "Update",
                "create": "Create"
      },
      "navigation": {
        "dashboard": "Dashboard",
        "knowledge bases": "Knowledge Bases",
        "conversations": "Conversations",
        "training": "Training",
        "settings": "Settings",
        "logout": "Logout"
      },
      "auth": {
        "login": "Login",
        "register": "Register",
        "email": "Email",
        "password": "Password",
        "name": "Name",
        "confirmPassword": "Confirm Password",
        "forgotPassword": "Forgot Password?",
        "rememberMe": "Remember Me",
        "loginSuccess": "Login successful",
        "registerSuccess": "Registration successful",
        "loginFailed": "Login failed",
        "registerFailed": "Registration failed",
        "invalidCredentials": "Invalid credentials",
        "emailRequired": "Email is required",
        "passwordRequired": "Password is required",
        "nameRequired": "Name is required",
        "passwordMismatch": "Passwords do not match",
        "emailInvalid": "Invalid email format",
        "passwordMinLength": "Password must be at least 6 characters",
        "confirmPasswordRequired": "Please confirm your password",
        "noAccount": "Don't have an account?",
        "hasAccount": "Already have an account?"
      },
      "dashboard": {
        "title": "Dashboard",
        "welcome": "Welcome to Pitto",
        "totalKnowledgeBases": "Total Knowledge Bases",
        "totalServices": "Total Services",
        "activeConversations": "Active Conversations",
        "totalMessages": "Total Messages",
        "responseTime": "Average Response Time",
        "recentActivity": "Recent Activity",
        "quickActions": "Quick Actions",
        "createKnowledgeBase": "Create Knowledge Base",
        "createService": "Create Service",
        "viewConversations": "View Conversations",
        "manageTraining": "Manage Training Data",
        "waitingForManual": "Waiting for Manual",
        "recentKnowledgeBases": "Recent Knowledge Bases",
        "recentConversations": "Recent Conversations"
      },
      "services": {
        "title": "Services",
        "createService": "Create Service",
        "editService": "Edit Service",
        "serviceName": "Service Name",
        "description": "Description",
        "apiKey": "API Key",
        "status": "Status",
        "active": "Active",
        "inactive": "Inactive",
        "createdAt": "Created At",
        "actions": "Actions",
        "copyApiKey": "Copy API Key",
        "testService": "Test Service",
        "deleteService": "Delete Service",
        "confirmDelete": "Are you sure you want to delete this service?",
        "serviceCreated": "Service created successfully",
        "serviceUpdated": "Service updated successfully",
        "serviceDeleted": "Service deleted successfully",
        "noServices": "No services found",
        "createFirstService": "Create your first service to get started",
        "fetchFailed": "Failed to fetch services",
        "updateFailed": "Failed to update service",
        "createFailed": "Failed to create service",
        "deleteFailed": "Failed to delete service",
        "statusUpdated": "Status updated successfully",
        "statusUpdateFailed": "Failed to update status",
        "apiKeyCopied": "API key copied to clipboard",
        "apiKeyLabel": "API Key:",
        "apiKeyWarning": "Please save this key securely for embedding the chat widget",
        "embedCode": "Embed Code",
        "embedInstructions": "Add the following code to your website:",
        "serviceNameRequired": "Service name is required",
        "serviceNamePlaceholder": "Enter service name",
        "descriptionPlaceholder": "Enter service description",
                "aiProviderRequired": "Please select AI provider",
                "aiProviderPlaceholder": "Select AI provider",
                "customWebhook": "Custom Webhook",
                "aiProvider": "AI Provider",
                "aiConfig": "AI Configuration",
                "aiConfigTooltip": "JSON format configuration, such as API keys, models, etc.",
        "webhookUrl": "Webhook URL",
        "webhookTooltip": "Used when AI provider is set to webhook",
        "count": "services"
      },
      "knowledge bases": {
        "title": "Knowledge Bases",
        "subtitle": "Manage your customer conversation knowledge bases",
        "createKnowledgeBase": "Create Knowledge Base",
        "name": "Name",
        "description": "Description",
        "status": "Status",
        "active": "Active",
        "inactive": "Inactive",
        "createdAt": "Created At",
        "actions": "Actions",
        "embedCode": "Embed Code",
        "confirmDelete": "Are you sure you want to delete this knowledge base?",
        "fetchFailed": "Failed to fetch knowledge bases",
        "createdSuccess": "Knowledge Base created successfully",
        "createFailed": "Failed to create knowledge base",
        "updatedSuccess": "Knowledge Base updated successfully",
        "updateFailed": "Failed to update knowledge base",
        "deletedSuccess": "Knowledge Base deleted successfully",
        "deleteFailed": "Failed to delete knowledge base",
        "embedCodeFailed": "Failed to generate embed code",
        "copiedToClipboard": "Copied to clipboard",
        "count": "knowledge bases"
      },
      "conversations": {
        "title": "Conversations",
        "conversationId": "Conversation ID",
        "visitorId": "Visitor ID",
        "status": "Status",
        "messages": "Messages",
        "startedAt": "Started At",
        "lastMessage": "Last Message",
        "viewConversation": "View Conversation",
        "noConversations": "No conversations found",
        "active": "Active",
        "resolved": "Resolved",
        "pending": "Pending",
        "fetchFailed": "Failed to fetch conversations",
        "fetchDetailsFailed": "Failed to fetch conversation details",
        "service": "Service",
        "unknownService": "Unknown Service",
        "actions": "Actions",
        "viewDetails": "View Details",
        "selectService": "Select Service",
        "details": "Conversation Details",
        "user": "User",
        "aiAssistant": "AI Assistant",
        "count": "conversations"
      },
      "training": {
        "title": "Training Data",
        "addTrainingData": "Add Training Data",
        "editTrainingData": "Edit Training Data",
        "question": "Question",
        "answer": "Answer",
        "category": "Category",
        "tags": "Tags",
        "isActive": "Active",
        "createdAt": "Created At",
        "actions": "Actions",
        "confirmDelete": "Are you sure you want to delete this training data?",
        "trainingDataAdded": "Training data added successfully",
        "trainingDataUpdated": "Training data updated successfully",
        "trainingDataDeleted": "Training data deleted successfully",
        "noTrainingData": "No training data found",
        "addFirstTrainingData": "Add your first training data to improve AI responses",
        "fetchFailed": "Failed to fetch training data",
        "updateFailed": "Failed to update training data",
        "createFailed": "Failed to create training data",
        "deleteFailed": "Failed to delete training data",
        "testInDevelopment": "Test feature is under development...",
        "testFailed": "Test failed",
        "testAI": "Test AI",
        "questionRequired": "Please enter a question",
        "questionPlaceholder": "Enter a question",
        "answerRequired": "Please enter an answer",
        "answerPlaceholder": "Enter an answer",
        "categoryPlaceholder": "Question category",
        "tagsTooltip": "Multiple tags separated by commas",
        "tagsPlaceholder": "tag1,tag2,tag3",
        "service": "Service",
        "aiTest": "AI Test",
        "selectService": "Select Service",
        "serviceRequired": "Please select a service",
        "servicePlaceholder": "Select a service to test",
        "testQuestion": "Test Question",
        "testQuestionRequired": "Please enter a test question",
        "testQuestionPlaceholder": "Enter a question to test",
        "aiResponse": "AI Response",
        "aiResponsePlaceholder": "AI response will be displayed here after clicking test",
        "startTest": "Start Test",
        "count": "training data"
      },
      "settings": {
        "title": "Settings",
        "profile": "Profile",
        "account": "Account",
        "preferences": "Preferences",
        "language": "Language",
        "theme": "Theme",
        "notifications": "Notifications",
        "security": "Security",
        "subscription": "Subscription",
        "billing": "Billing",
        "apiKeys": "API Keys",
        "webhooks": "Webhooks",
        "integrations": "Integrations",
        "saveChanges": "Save Changes",
        "changesSaved": "Changes saved successfully",
        "currentPlan": "Current Plan",
        "free": "Free",
        "pro": "Pro",
        "proMax": "Pro Max",
        "upgrade": "Upgrade",
        "downgrade": "Downgrade",
        "profileUpdated": "Profile updated successfully",
        "updateFailed": "Update failed",
        "unknown": "Unknown",
        "updateProfile": "Update Profile",
        "maxServices": "Max Services",
        "currentPlanFeatures": "Current Plan Features",
        "createServices": "Create {count} customer service",
        "unlimitedConversations": "Unlimited conversation records",
        "basicAITraining": "Basic AI training features",
        "emailSupport": "Email technical support",
        "viewDetails": "View Details",
        "accountActions": "Account Actions",
        "deleteAccount": "Delete Account",
        "usageStats": "Usage Statistics",
        "customerServices": "Customer Services",
        "totalConversations": "Total Conversations",
        "totalMessages": "Total Messages",
        "systemInfo": "System Information",
        "version": "Version",
        "lastUpdate": "Last Update",
        "apiStatus": "API Status",
        "normal": "Normal",
        "maxKnowledgeBases": "Max Knowledge Bases",
        "createKnowledgeBases": "Create {{count}} knowledge bases",
        "knowledge bases": "Knowledge Bases"
      },
      "chat": {
        "title": "Chat",
        "sendMessage": "Send Message",
        "typeMessage": "Type your message...",
        "connecting": "Connecting...",
        "connected": "Connected",
        "disconnected": "Disconnected",
        "typing": "Typing...",
        "online": "Online",
        "offline": "Offline",
        "welcome": "Hello! How can I help you today?",
        "error": "Sorry, there was an error processing your message.",
        "retry": "Retry",
        "clearChat": "Clear Chat",
        "downloadChat": "Download Chat",
        "endChat": "End Chat"
      },
      "errors": {
        "networkError": "Network error. Please check your connection.",
        "serverError": "Server error. Please try again later.",
        "unauthorized": "Unauthorized. Please login again.",
        "forbidden": "Access denied.",
        "notFound": "Resource not found.",
        "validationError": "Please check your input.",
        "unknownError": "An unknown error occurred."
      }
    },
  },
  'zh-CN': {
    translation: {
      "common": {
        "loading": "加载中...",
        "save": "保存",
        "cancel": "取消",
        "delete": "删除",
        "edit": "编辑",
        "add": "添加",
        "search": "搜索",
        "filter": "筛选",
        "refresh": "刷新",
        "confirm": "确认",
        "back": "返回",
        "next": "下一步",
        "previous": "上一步",
        "submit": "提交",
        "reset": "重置",
        "close": "关闭",
        "open": "打开",
        "yes": "是",
        "no": "否",
        "ok": "确定",
        "error": "错误",
        "success": "成功",
        "warning": "警告",
        "info": "信息",
                "view": "查看",
                "viewAll": "查看全部",
                "copy": "复制",
                "total": "总计",
                "update": "更新",
                "create": "创建"
      },
      "navigation": {
        "dashboard": "仪表板",
        "knowledge bases": "工作空间",
        "conversations": "对话",
        "training": "训练",
        "settings": "设置",
        "logout": "退出登录"
      },
      "auth": {
        "login": "登录",
        "register": "注册",
        "email": "邮箱",
        "password": "密码",
        "name": "姓名",
        "confirmPassword": "确认密码",
        "forgotPassword": "忘记密码？",
        "rememberMe": "记住我",
        "loginSuccess": "登录成功",
        "registerSuccess": "注册成功",
        "loginFailed": "登录失败",
        "registerFailed": "注册失败",
        "invalidCredentials": "用户名或密码错误",
        "emailRequired": "邮箱不能为空",
        "passwordRequired": "密码不能为空",
        "nameRequired": "姓名不能为空",
        "passwordMismatch": "两次输入的密码不一致",
        "emailInvalid": "邮箱格式不正确",
        "passwordMinLength": "密码至少6位",
        "confirmPasswordRequired": "请确认密码",
        "noAccount": "还没有账号？",
        "hasAccount": "已有账号？"
      },
      "dashboard": {
        "title": "仪表板",
        "welcome": "欢迎使用Pitto",
        "totalKnowledgeBases": "工作空间总数",
        "totalServices": "总服务数",
        "activeConversations": "活跃对话",
        "totalMessages": "总消息数",
        "responseTime": "平均响应时间",
        "recentActivity": "最近活动",
        "quickActions": "快速操作",
        "createKnowledgeBase": "创建工作空间",
        "createService": "创建服务",
        "viewConversations": "查看对话",
        "manageTraining": "管理训练数据",
        "waitingForManual": "等待人工处理",
        "recentKnowledgeBases": "最近的工作空间",
        "recentConversations": "最近的对话"
      },
      "services": {
        "title": "服务管理",
        "createService": "创建服务",
        "editService": "编辑服务",
        "serviceName": "服务名称",
        "description": "描述",
        "apiKey": "API密钥",
        "status": "状态",
        "active": "活跃",
        "inactive": "非活跃",
        "createdAt": "创建时间",
        "actions": "操作",
        "copyApiKey": "复制API密钥",
        "testService": "测试服务",
        "deleteService": "删除服务",
        "confirmDelete": "确定要删除这个服务吗？",
        "serviceCreated": "服务创建成功",
        "serviceUpdated": "服务更新成功",
        "serviceDeleted": "服务删除成功",
        "noServices": "暂无服务",
        "createFirstService": "创建您的第一个服务开始使用",
        "fetchFailed": "获取服务列表失败",
        "updateFailed": "服务更新失败",
        "createFailed": "服务创建失败",
        "deleteFailed": "服务删除失败",
        "statusUpdated": "状态更新成功",
        "statusUpdateFailed": "状态更新失败",
        "apiKeyCopied": "API密钥已复制到剪贴板",
        "apiKeyLabel": "API密钥：",
        "apiKeyWarning": "请妥善保存此密钥，用于嵌入聊天窗口",
        "embedCode": "嵌入代码",
        "embedInstructions": "将以下代码添加到您的网站：",
        "serviceNameRequired": "请输入服务名称",
        "serviceNamePlaceholder": "请输入服务名称",
        "descriptionPlaceholder": "请输入服务描述",
                "aiProviderRequired": "请选择AI提供商",
                "aiProviderPlaceholder": "请选择AI提供商",
                "customWebhook": "自定义Webhook",
                "aiProvider": "AI提供商",
                "aiConfig": "AI配置",
                "aiConfigTooltip": "JSON格式的配置信息，如API密钥、模型等",
        "webhookUrl": "Webhook地址",
        "webhookTooltip": "当AI提供商选择为webhook时使用",
        "count": "个服务"
      },
      "knowledge bases": {
        "title": "工作空间",
        "subtitle": "管理您的客户对话工作空间",
        "createKnowledgeBase": "创建工作空间",
        "name": "名称",
        "description": "描述",
        "status": "状态",
        "active": "活跃",
        "inactive": "非活跃",
        "createdAt": "创建时间",
        "actions": "操作",
        "embedCode": "嵌入代码",
        "confirmDelete": "确定要删除这个工作空间吗？",
        "fetchFailed": "获取工作空间列表失败",
        "createdSuccess": "工作空间创建成功",
        "createFailed": "工作空间创建失败",
        "updatedSuccess": "工作空间更新成功",
        "updateFailed": "工作空间更新失败",
        "deletedSuccess": "工作空间删除成功",
        "deleteFailed": "工作空间删除失败",
        "embedCodeFailed": "生成嵌入代码失败",
        "copiedToClipboard": "已复制到剪贴板",
        "count": "个工作空间"
      },
      "conversations": {
        "title": "对话管理",
        "conversationId": "对话ID",
        "visitorId": "访客ID",
        "status": "状态",
        "messages": "消息数",
        "startedAt": "开始时间",
        "lastMessage": "最后消息",
        "viewConversation": "查看对话",
        "noConversations": "暂无对话",
        "active": "活跃",
        "resolved": "已解决",
        "pending": "待处理",
        "fetchFailed": "获取对话列表失败",
        "fetchDetailsFailed": "获取对话详情失败",
        "service": "服务",
        "unknownService": "未知服务",
        "actions": "操作",
        "viewDetails": "查看详情",
        "selectService": "选择服务",
        "details": "对话详情",
        "user": "用户",
        "aiAssistant": "AI助手",
        "count": "条对话"
      },
      "training": {
        "title": "训练数据",
        "addTrainingData": "添加训练数据",
        "editTrainingData": "编辑训练数据",
        "question": "问题",
        "answer": "答案",
        "category": "分类",
        "tags": "标签",
        "isActive": "启用",
        "createdAt": "创建时间",
        "actions": "操作",
        "confirmDelete": "确定要删除这条训练数据吗？",
        "trainingDataAdded": "训练数据添加成功",
        "trainingDataUpdated": "训练数据更新成功",
        "trainingDataDeleted": "训练数据删除成功",
        "noTrainingData": "暂无训练数据",
        "addFirstTrainingData": "添加您的第一条训练数据来改善AI回复",
        "fetchFailed": "获取训练数据失败",
        "updateFailed": "训练数据更新失败",
        "createFailed": "训练数据创建失败",
        "deleteFailed": "训练数据删除失败",
        "testInDevelopment": "测试功能开发中...",
        "testFailed": "测试失败",
        "testAI": "测试AI",
        "questionRequired": "请输入问题",
        "questionPlaceholder": "请输入问题",
        "answerRequired": "请输入答案",
        "answerPlaceholder": "请输入答案",
        "categoryPlaceholder": "问题分类",
        "tagsTooltip": "多个标签用逗号分隔",
        "tagsPlaceholder": "标签1,标签2,标签3",
        "service": "服务",
        "aiTest": "AI测试",
        "selectService": "选择服务",
        "serviceRequired": "请选择服务",
        "servicePlaceholder": "请选择要测试的服务",
        "testQuestion": "测试问题",
        "testQuestionRequired": "请输入测试问题",
        "testQuestionPlaceholder": "请输入要测试的问题",
        "aiResponse": "AI回复",
        "aiResponsePlaceholder": "点击测试后，AI的回复将显示在这里",
        "startTest": "开始测试",
        "count": "条训练数据"
      },
      "settings": {
        "title": "设置",
        "profile": "个人资料",
        "account": "账户",
        "preferences": "偏好设置",
        "language": "语言",
        "theme": "主题",
        "notifications": "通知",
        "security": "安全",
        "subscription": "订阅",
        "billing": "账单",
        "apiKeys": "API密钥",
        "webhooks": "Webhooks",
        "integrations": "集成",
        "saveChanges": "保存更改",
        "changesSaved": "更改保存成功",
        "currentPlan": "当前计划",
        "free": "免费版",
        "pro": "专业版",
        "proMax": "Pro Max",
        "upgrade": "升级",
        "downgrade": "降级",
        "profileUpdated": "个人信息更新成功",
        "updateFailed": "更新失败",
        "unknown": "未知",
        "updateProfile": "更新信息",
        "maxServices": "最大服务数",
        "currentPlanFeatures": "当前计划功能",
        "createServices": "创建 {count} 个客服服务",
        "unlimitedConversations": "无限制对话记录",
        "basicAITraining": "基础AI训练功能",
        "emailSupport": "邮件技术支持",
        "viewDetails": "查看详情",
        "accountActions": "账户操作",
        "deleteAccount": "删除账户",
        "usageStats": "使用统计",
        "customerServices": "客服服务",
        "totalConversations": "总对话数",
        "totalMessages": "总消息数",
        "systemInfo": "系统信息",
        "version": "版本",
        "lastUpdate": "最后更新",
        "apiStatus": "API状态",
        "normal": "正常",
        "maxKnowledgeBases": "最大工作空间数",
        "createKnowledgeBases": "创建 {{count}} 个工作空间",
        "knowledge bases": "工作空间"
      },
      "chat": {
        "title": "聊天",
        "sendMessage": "发送消息",
        "typeMessage": "输入您的消息...",
        "connecting": "连接中...",
        "connected": "已连接",
        "disconnected": "已断开",
        "typing": "正在输入...",
        "online": "在线",
        "offline": "离线",
        "welcome": "您好！今天我能为您做些什么？",
        "error": "抱歉，处理您的消息时出现错误。",
        "retry": "重试",
        "clearChat": "清空聊天",
        "downloadChat": "下载聊天记录",
        "endChat": "结束聊天"
      },
      "errors": {
        "networkError": "网络错误，请检查您的网络连接。",
        "serverError": "服务器错误，请稍后重试。",
        "unauthorized": "未授权，请重新登录。",
        "forbidden": "访问被拒绝。",
        "notFound": "资源未找到。",
        "validationError": "请检查您的输入。",
        "unknownError": "发生未知错误。"
      }
    },
  },
  'ja-JP': {
    translation: {
      "common": {
        "loading": "読み込み中...",
        "save": "保存",
        "cancel": "キャンセル",
        "delete": "削除",
        "edit": "編集",
        "add": "追加",
        "search": "検索",
        "filter": "フィルター",
        "refresh": "更新",
        "confirm": "確認",
        "back": "戻る",
        "next": "次へ",
        "previous": "前へ",
        "submit": "送信",
        "reset": "リセット",
        "close": "閉じる",
        "open": "開く",
        "yes": "はい",
        "no": "いいえ",
        "ok": "OK",
        "error": "エラー",
        "success": "成功",
        "warning": "警告",
        "info": "情報",
                "view": "表示",
                "viewAll": "すべて表示",
                "copy": "コピー",
                "total": "合計",
                "update": "更新",
                "create": "作成"
      },
      "navigation": {
        "dashboard": "ダッシュボード",
        "knowledge bases": "ワークスペース",
        "conversations": "会話",
        "training": "トレーニング",
        "settings": "設定",
        "logout": "ログアウト"
      },
      "auth": {
        "login": "ログイン",
        "register": "登録",
        "email": "メールアドレス",
        "password": "パスワード",
        "name": "名前",
        "confirmPassword": "パスワード確認",
        "forgotPassword": "パスワードを忘れましたか？",
        "rememberMe": "ログイン状態を保持",
        "loginSuccess": "ログインしました",
        "registerSuccess": "登録しました",
        "loginFailed": "ログインに失敗しました",
        "registerFailed": "登録に失敗しました",
        "invalidCredentials": "メールアドレスまたはパスワードが正しくありません",
        "emailRequired": "メールアドレスは必須です",
        "passwordRequired": "パスワードは必須です",
        "nameRequired": "名前は必須です",
        "passwordMismatch": "パスワードが一致しません",
        "emailInvalid": "メールアドレスの形式が正しくありません",
        "passwordMinLength": "パスワードは6文字以上である必要があります",
        "confirmPasswordRequired": "パスワードを確認してください",
        "noAccount": "アカウントをお持ちでない方は？",
        "hasAccount": "すでにアカウントをお持ちの方は？"
      },
      "dashboard": {
        "title": "ダッシュボード",
        "welcome": "Pittoへようこそ",
        "totalKnowledgeBases": "ワークスペース合計",
        "totalServices": "総サービス数",
        "activeConversations": "アクティブな会話",
        "totalMessages": "総メッセージ数",
        "responseTime": "平均応答時間",
        "recentActivity": "最近のアクティビティ",
        "quickActions": "クイックアクション",
        "createKnowledgeBase": "ワークスペース作成",
        "createService": "サービス作成",
        "viewConversations": "会話を表示",
        "manageTraining": "トレーニングデータ管理",
        "waitingForManual": "手動処理待ち",
        "recentKnowledgeBases": "最近のワークスペース",
        "recentConversations": "最近の会話"
      },
      "services": {
        "title": "サービス管理",
        "createService": "サービス作成",
        "editService": "サービス編集",
        "serviceName": "サービス名",
        "description": "説明",
        "apiKey": "APIキー",
        "status": "ステータス",
        "active": "アクティブ",
        "inactive": "非アクティブ",
        "createdAt": "作成日時",
        "actions": "アクション",
        "copyApiKey": "APIキーをコピー",
        "testService": "サービスをテスト",
        "deleteService": "サービスを削除",
        "confirmDelete": "このサービスを削除してもよろしいですか？",
        "serviceCreated": "サービスが作成されました",
        "serviceUpdated": "サービスが更新されました",
        "serviceDeleted": "サービスが削除されました",
        "noServices": "サービスが見つかりません",
        "createFirstService": "最初のサービスを作成して始めましょう",
        "fetchFailed": "サービスの取得に失敗しました",
        "updateFailed": "サービスの更新に失敗しました",
        "createFailed": "サービスの作成に失敗しました",
        "deleteFailed": "サービスの削除に失敗しました",
        "statusUpdated": "ステータスが更新されました",
        "statusUpdateFailed": "ステータスの更新に失敗しました",
        "apiKeyCopied": "APIキーがクリップボードにコピーされました",
        "apiKeyLabel": "APIキー：",
        "apiKeyWarning": "チャットウィジェットの埋め込みに使用するため、このキーを安全に保存してください",
        "embedCode": "埋め込みコード",
        "embedInstructions": "以下のコードをあなたのウェブサイトに追加してください：",
        "serviceNameRequired": "サービス名を入力してください",
        "serviceNamePlaceholder": "サービス名を入力",
        "descriptionPlaceholder": "サービス説明を入力",
                "aiProviderRequired": "AIプロバイダーを選択してください",
                "aiProviderPlaceholder": "AIプロバイダーを選択",
                "customWebhook": "カスタムWebhook",
                "aiProvider": "AIプロバイダー",
                "aiConfig": "AI設定",
                "aiConfigTooltip": "APIキー、モデルなどのJSON形式の設定情報",
        "webhookUrl": "Webhook URL",
        "webhookTooltip": "AIプロバイダーがwebhookに設定されている場合に使用",
        "count": "サービス"
      },
      "knowledge bases": {
        "title": "ワークスペース",
        "subtitle": "お客様の会話ワークスペースを管理",
        "createKnowledgeBase": "ワークスペース作成",
        "name": "名前",
        "description": "説明",
        "status": "ステータス",
        "active": "アクティブ",
        "inactive": "非アクティブ",
        "createdAt": "作成日時",
        "actions": "アクション",
        "embedCode": "埋め込みコード",
        "confirmDelete": "このワークスペースを削除してもよろしいですか？",
        "fetchFailed": "ワークスペースの取得に失敗しました",
        "createdSuccess": "ワークスペースが作成されました",
        "createFailed": "ワークスペースの作成に失敗しました",
        "updatedSuccess": "ワークスペースが更新されました",
        "updateFailed": "ワークスペースの更新に失敗しました",
        "deletedSuccess": "ワークスペースが削除されました",
        "deleteFailed": "ワークスペースの削除に失敗しました",
        "embedCodeFailed": "埋め込みコードの生成に失敗しました",
        "copiedToClipboard": "クリップボードにコピーされました",
        "count": "ワークスペース"
      },
      "conversations": {
        "title": "会話管理",
        "conversationId": "会話ID",
        "visitorId": "訪問者ID",
        "status": "ステータス",
        "messages": "メッセージ数",
        "startedAt": "開始日時",
        "lastMessage": "最後のメッセージ",
        "viewConversation": "会話を表示",
        "noConversations": "会話が見つかりません",
        "active": "アクティブ",
        "resolved": "解決済み",
        "pending": "保留中",
        "fetchFailed": "会話の取得に失敗しました",
        "fetchDetailsFailed": "会話詳細の取得に失敗しました",
        "service": "サービス",
        "unknownService": "不明なサービス",
        "actions": "アクション",
        "viewDetails": "詳細を表示",
        "selectService": "サービスを選択",
        "details": "会話詳細",
        "user": "ユーザー",
        "aiAssistant": "AIアシスタント",
        "count": "会話"
      },
      "training": {
        "title": "トレーニングデータ",
        "addTrainingData": "トレーニングデータを追加",
        "editTrainingData": "トレーニングデータを編集",
        "question": "質問",
        "answer": "回答",
        "category": "カテゴリ",
        "tags": "タグ",
        "isActive": "アクティブ",
        "createdAt": "作成日時",
        "actions": "アクション",
        "confirmDelete": "このトレーニングデータを削除してもよろしいですか？",
        "trainingDataAdded": "トレーニングデータが追加されました",
        "trainingDataUpdated": "トレーニングデータが更新されました",
        "trainingDataDeleted": "トレーニングデータが削除されました",
        "noTrainingData": "トレーニングデータが見つかりません",
        "addFirstTrainingData": "AIの応答を改善するために最初のトレーニングデータを追加しましょう",
        "fetchFailed": "トレーニングデータの取得に失敗しました",
        "updateFailed": "トレーニングデータの更新に失敗しました",
        "createFailed": "トレーニングデータの作成に失敗しました",
        "deleteFailed": "トレーニングデータの削除に失敗しました",
        "testInDevelopment": "テスト機能は開発中です...",
        "testFailed": "テストに失敗しました",
        "testAI": "AIをテスト",
        "questionRequired": "質問を入力してください",
        "questionPlaceholder": "質問を入力",
        "answerRequired": "回答を入力してください",
        "answerPlaceholder": "回答を入力",
        "categoryPlaceholder": "質問カテゴリ",
        "tagsTooltip": "複数のタグはカンマで区切ってください",
        "tagsPlaceholder": "タグ1,タグ2,タグ3",
        "service": "サービス",
        "aiTest": "AIテスト",
        "selectService": "サービスを選択",
        "serviceRequired": "サービスを選択してください",
        "servicePlaceholder": "テストするサービスを選択",
        "testQuestion": "テスト質問",
        "testQuestionRequired": "テスト質問を入力してください",
        "testQuestionPlaceholder": "テストする質問を入力",
        "aiResponse": "AI応答",
        "aiResponsePlaceholder": "テストをクリック後、AIの応答がここに表示されます",
        "startTest": "テスト開始",
        "count": "トレーニングデータ"
      },
      "settings": {
        "title": "設定",
        "profile": "プロフィール",
        "account": "アカウント",
        "preferences": "設定",
        "language": "言語",
        "theme": "テーマ",
        "notifications": "通知",
        "security": "セキュリティ",
        "subscription": "サブスクリプション",
        "billing": "請求",
        "apiKeys": "APIキー",
        "webhooks": "Webhooks",
        "integrations": "統合",
        "saveChanges": "変更を保存",
        "changesSaved": "変更が保存されました",
        "currentPlan": "現在のプラン",
        "free": "無料",
        "pro": "プロ",
        "proMax": "Pro Max",
        "upgrade": "アップグレード",
        "downgrade": "ダウングレード",
        "profileUpdated": "プロフィールが更新されました",
        "updateFailed": "更新に失敗しました",
        "unknown": "不明",
        "updateProfile": "プロフィールを更新",
        "maxServices": "最大サービス数",
        "currentPlanFeatures": "現在のプラン機能",
        "createServices": "{count}個のカスタマーサービスを作成",
        "unlimitedConversations": "無制限の会話記録",
        "basicAITraining": "基本的なAIトレーニング機能",
        "emailSupport": "メール技術サポート",
        "viewDetails": "詳細を表示",
        "accountActions": "アカウント操作",
        "deleteAccount": "アカウントを削除",
        "usageStats": "使用統計",
        "customerServices": "カスタマーサービス",
        "totalConversations": "総会話数",
        "totalMessages": "総メッセージ数",
        "systemInfo": "システム情報",
        "version": "バージョン",
        "lastUpdate": "最終更新",
        "apiStatus": "APIステータス",
        "normal": "正常",
        "maxKnowledgeBases": "最大ワークスペース数",
        "createKnowledgeBases": "{{count}}個のワークスペースを作成",
        "knowledge bases": "ワークスペース"
      },
      "chat": {
        "title": "チャット",
        "sendMessage": "メッセージを送信",
        "typeMessage": "メッセージを入力...",
        "connecting": "接続中...",
        "connected": "接続済み",
        "disconnected": "切断済み",
        "typing": "入力中...",
        "online": "オンライン",
        "offline": "オフライン",
        "welcome": "こんにちは！今日はどのようにお手伝いできますか？",
        "error": "申し訳ございません。メッセージの処理中にエラーが発生しました。",
        "retry": "再試行",
        "clearChat": "チャットをクリア",
        "downloadChat": "チャットをダウンロード",
        "endChat": "チャットを終了"
      },
      "errors": {
        "networkError": "ネットワークエラー。接続を確認してください。",
        "serverError": "サーバーエラー。後でもう一度お試しください。",
        "unauthorized": "認証されていません。再度ログインしてください。",
        "forbidden": "アクセスが拒否されました。",
        "notFound": "リソースが見つかりません。",
        "validationError": "入力内容を確認してください。",
        "unknownError": "不明なエラーが発生しました。"
      }
    },
  },
};
*/

// Public marketing/portal pages (incl. /try) default to Japanese when the
// visitor has made no explicit choice — pitto.jp is Japan-first, and this makes
// crawlers render these pages fully in Japanese to match the ja title/meta baked
// by scripts/prerender-seo.mjs. The logged-in dashboard is unaffected: it keeps
// browser-language detection. This default is intentionally NOT cached to
// localStorage, so it never leaks into the dashboard, and an explicit switch
// (LanguageContext.setLanguage) still wins everywhere.
const MARKETING_PATHS = [
  '/', '/try', '/features', '/pricing', '/contact', '/mcp', '/news',
  '/kumamoto',
  // The free tools and the comparison pages are the Japan-first acquisition
  // funnel; without them here they fell through to `navigator` and rendered in
  // English for Googlebot while serving ja title/description/canonical.
  '/tools', '/compare', '/line-ai-chatbot',
  '/docs', '/privacy', '/terms', '/tokushoho', '/cookies', '/dpa', '/aup',
];
const isMarketingPath = (p: string) =>
  p === '/' || MARKETING_PATHS.some((base) => base !== '/' && (p === base || p.startsWith(base + '/')));

const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'marketingDefault',
  lookup() {
    try {
      if (typeof window !== 'undefined' && isMarketingPath(window.location.pathname)) {
        return 'ja-JP';
      }
    } catch {
      /* no window — skip */
    }
    return undefined;
  },
});

const initPromise = i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {},
    fallbackLng: 'en-US',
    debug: process.env.NODE_ENV === 'development',

    detection: {
      // localStorage (explicit choice) wins; then Japanese default on marketing
      // pages; then the browser language (dashboard + any non-marketing route).
      // No `caches` — the auto-default must not persist.
      order: ['localStorage', 'marketingDefault', 'navigator', 'htmlTag'],
      // Explicit []: omitting this key re-enables the detector's DEFAULT
      // ['localStorage'], which would persist the auto-default and leak it into
      // the dashboard. Only LanguageContext.setLanguage (explicit switch) writes.
      caches: [],
      lookupLocalStorage: 'pitto-language',
    },

    interpolation: {
      escapeValue: false,
    },
  });

// Fetches a locale tree and registers it. Must be awaited before anything
// renders with `t()` and before every changeLanguage — otherwise the UI paints
// raw translation keys. Idempotent: a language already registered is a no-op.
export async function loadLocale(language: string): Promise<void> {
  const load = localeLoaders[language];
  if (!load || i18n.hasResourceBundle(language, 'translation')) {
    return;
  }
  const { default: translation } = await load();
  i18n.addResourceBundle(language, 'translation', translation, true, true);
}

// Resolves once the detected language is usable. index.tsx waits on this before
// mounting.
export const i18nReady = initPromise.then(() => loadLocale(i18n.language));

export default i18n;
